import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const QUEST_FEATURED_GALLERY_ID = "6a1f2a3cd388b57911134962";

function imageUrl(path) {
  if (!path) return "";
  return path.startsWith("http") ? path : path;
}

const GalleryCardSkeleton = () => (
  <div
    className="relative overflow-hidden border border-black/5 bg-white shadow-sm"
    style={{ height: "500px", borderRadius: "12px" }}
  >
    <div className="h-full w-full animate-pulse bg-gradient-to-br from-[#e8eeee] via-[#f7fafa] to-[#dce5e5]" />
    <div className="absolute bottom-8 right-4 w-[76%] space-y-3">
      <div className="ml-auto h-8 w-2/3 animate-pulse rounded bg-white/80" />
      <div className="ml-auto h-3 w-full animate-pulse rounded bg-white/65" />
      <div className="ml-auto h-3 w-4/5 animate-pulse rounded bg-white/55" />
      <div className="ml-auto mt-5 h-3 w-1/2 animate-pulse rounded bg-[#9eeff4]/80" />
    </div>
  </div>
);

function Gallery({ maxItems = null }) {
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function fetchCategories() {
      try {
        setLoadingCategories(true);
        const response = await fetch("/api/gallery/categories");
        const result = await response.json();
        if (!result.success || !Array.isArray(result.data) || result.data.length === 0) return;

        if (isMounted) {
          setCategories(
          result.data.map((category) => ({
            key: category.key,
            title: category.title,
            description: category.description,
            image: imageUrl(category.image || category.featuredGalleryCoverImage),
            featuredGalleryId: category.featuredGalleryId || null,
          }))
        );
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.warn("Unable to load gallery categories", error);
        }
      } finally {
        if (isMounted) setLoadingCategories(false);
      }
    }

    fetchCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  const displayedCategories = useMemo(
    () => (maxItems ? categories.slice(0, maxItems) : categories),
    [categories, maxItems]
  );

  return (
    <section className="main-gallery bg-[#F5F5DB] mt-5 pb-[80px] px-[1rem] rounded-[30px]">
      <div className="container m-auto">
        <h2 className="text-[#000] font-cinzel pt-[15px] pb-[15px] text-[40px] lg:text-[80px] text-center">
          Gallery Collection Categories
        </h2>
        <p className="mx-auto mb-8 max-w-3xl text-center font-outfit text-sm leading-7 text-[#536061]">
          Start with a collection category, then open a collection to view its image collections and published images.
        </p>

        {loadingCategories && displayedCategories.length === 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: maxItems || 4 }).map((_, index) => (
              <GalleryCardSkeleton key={index} />
            ))}
          </div>
        ) : displayedCategories.length === 0 ? (
          <p className="py-10 text-center font-outfit text-sm text-gray-500">No gallery categories are available yet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {displayedCategories.map(({ key, title, description, image, featuredGalleryId }) => (
            <Link
              key={key}
              to={featuredGalleryId
                  ? `/gallery/${key}/${featuredGalleryId}`
                  : key === "quests"
                    ? `/gallery/quests/${QUEST_FEATURED_GALLERY_ID}`
                  : `/gallery/${key}`}
              className="group block"
            >
              <div
                className="relative overflow-hidden border border-black/5 bg-white shadow-sm transition duration-300 group-hover:-translate-y-1 group-hover:shadow-xl"
                style={{ height: "500px", borderRadius: "12px" }}
              >
                {image ? (
                  <img
                    src={image}
                    className="h-full w-full object-cover transition duration-500 scale-105 group-hover:scale-110"
                    alt={title}
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-[#dce5e5] via-[#f7fafa] to-[#c9d7da]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/78 via-black/18 to-transparent" />
                <div className="absolute bottom-8 right-4 max-w-[84%] text-right">
                  <h3 className="font-cinzel text-[30px] leading-tight text-white">{title}</h3>
                  <p className="mt-2 font-outfit text-sm leading-5 text-white">{description}</p>
                  <p className="mt-4 font-outfit text-xs font-semibold uppercase tracking-[0.16em] text-[#00F0FB]">
                    View collections
                  </p>
                </div>
              </div>
            </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default Gallery;
